import React from 'react';
import { PERMISSIONS } from 'constants/permissions';
import { HasAccess } from 'components/common/accessControl';
import { Button, Col, Image, MultiLineEllipsisText, Row, Tag, Typography } from 'components';
import { EditIconButton } from 'components/common/icons';
import styles from './styles.module.scss';
import { useDetails } from './useDetails';
import { ItemDetailsEdit } from './edit';

const { Text, Paragraph } = Typography;
export const Details = React.memo(() => {
  const { isOpen, onModalClose, onModalOpen, itemDetails, onRedirectToSupplier, onRedirectToBrand } = useDetails();
  return (
    <>
      <Row gutter={[0, 8]}>
        <Col span={24} className={styles.main_info}>
          <Row justify={'space-between'} wrap={false}>
            <Col span={20}>
              <Row gutter={[0, 4]}>
                <Col span={24} className={styles.id_container}>
                  <Text className={'font-size-12'} type={'secondary'} ellipsis={{ tooltip: true }}>
                    Item Number: {itemDetails.uuid}
                  </Text>
                </Col>
                <Col span={24}>
                  <Text className={'font-size-18 font-weight-700'} ellipsis={{ tooltip: true }}>
                    {itemDetails.itemSKU}
                  </Text>
                </Col>
                <Col span={24} className={'margin-t-4'}>
                  <Paragraph className={'font-size-12'} type={'secondary'} ellipsis={{ rows: 3, tooltip: true }}>
                    {itemDetails.description}
                  </Paragraph>
                </Col>
              </Row>
            </Col>
            <Col>
              <HasAccess role={PERMISSIONS.items.item.write}>
                <Button type={'link'} icon={<EditIconButton />} onClick={onModalOpen} />
              </HasAccess>
            </Col>
          </Row>
        </Col>
        <Col span={24} className={styles.additional_info}>
          <Row gutter={[0, 16]} className={'margin-t-4'}>
            <Col span={24}>
              <Row style={{ gap: '9px' }}>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {itemDetails.formattedCreatedDate}
                </Text>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {itemDetails.formattedLastModifiedDate}
                </Text>
              </Row>
            </Col>
            {itemDetails.manualTags?.length && (
              <Col span={24}>
                <Row className={styles.tag_container}>
                  {itemDetails.manualTags?.map((_tag: string, index: number) => (
                    <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                      {_tag}
                    </Tag>
                  ))}
                </Row>
              </Col>
            )}
            <Col span={24} className={styles.expiry_section}>
              <Row>
                <Col span={24}>
                  <Text type={'secondary'} className={'font-weight-500'}>
                    Expiry Date: {itemDetails.expiryDate || 'Not Available'}
                  </Text>
                </Col>
                <Col span={24}>
                  <Text type={'secondary'} className={'font-weight-500'}>
                    Price Range:{' '}
                    {itemDetails.minPrice && itemDetails.maxPrice
                      ? itemDetails.minPrice === itemDetails.maxPrice
                        ? `€${itemDetails.minPrice}`
                        : `€${itemDetails.minPrice}-${itemDetails.maxPrice}`
                      : itemDetails.minPrice
                        ? `€${itemDetails.minPrice} Min`
                        : itemDetails.maxPrice
                          ? `€${itemDetails.leadTimeMax} Max`
                          : 'Not Available'}
                  </Text>
                </Col>
                <Col span={24}>
                  <Text type={'secondary'} className={'font-weight-500'}>
                    Latest Price: {itemDetails.latestPrice ? `€${itemDetails.latestPrice}` : 'Not Available'}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={[0, 10]}>
            <Col span={24}>
              <Row wrap={false} gutter={4} align={'middle'}>
                {itemDetails.brandInfo?.files?.LOGO && (
                  <Col className={styles.image_wrapper}>
                    <Image src={itemDetails.brandInfo.files.LOGO} preview={false} alt={'Logo'} />
                  </Col>
                )}
                <Col flex={1}>
                  <Text
                    ellipsis={{ tooltip: true }}
                    className={'font-weight-500 pointer'}
                    style={{ color: '#05796B', whiteSpace: 'nowrap' }}
                    onClick={() => onRedirectToBrand(itemDetails.brandInfo.id)}
                  >
                    {itemDetails.brandInfo?.name}
                  </Text>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Row>
                <Col span={24}>
                  <Row wrap={false} className={'gap-2'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} className={'font-weight-500'}>
                      Main Supplier:
                    </Text>
                    {itemDetails.brandInfo?.mainSupplier?.name ? (
                      <Text
                        underline
                        type={'secondary'}
                        className={'pointer font-weight-500'}
                        ellipsis={{ tooltip: true }}
                        onClick={() => onRedirectToSupplier(itemDetails.brandInfo.mainSupplier.id)}
                      >
                        {itemDetails.brandInfo?.mainSupplier?.name}
                      </Text>
                    ) : (
                      <Text type={'secondary'} className={'font-weight-500'}>
                        Not Set
                      </Text>
                    )}
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} className={'gap-2'}>
                    <Col>
                      <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }} className={'font-weight-500'}>
                        Price reference supplier:
                      </Text>
                    </Col>
                    {itemDetails.brandInfo?.priceReferenceSupplier?.name ? (
                      <Text
                        underline
                        type={'secondary'}
                        className={'pointer font-weight-500'}
                        ellipsis={{ tooltip: true }}
                        onClick={() => onRedirectToSupplier(itemDetails.brandInfo.priceReferenceSupplier.id)}
                      >
                        {itemDetails.brandInfo?.priceReferenceSupplier?.name}
                      </Text>
                    ) : (
                      <Text type={'secondary'} className={'font-weight-500'}>
                        Not Set
                      </Text>
                    )}
                  </Row>
                </Col>
                <Col span={24}>
                  <MultiLineEllipsisText subTitle={'Additional Notes:'} text={itemDetails.additionalNotes} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <ItemDetailsEdit open={isOpen} onCancel={onModalClose} />
    </>
  );
});
